import { useCallback, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Transaction, TransactionIssueType, TransactionTypeValue } from '../../../services/transaction.types';

export interface TransactionFilters {
    search?: string;
    types?: TransactionTypeValue[];
    accounts?: string[];
    assets?: string[];
    flags?: TransactionIssueType[];
    dates?: {
        start?: string;
        end?: string;
    };
}

const parseUrlToFilters = (params: URLSearchParams): TransactionFilters => {
    const filters: TransactionFilters = {};
    const entries = Object.fromEntries(params.entries());

    if (entries.search) filters.search = entries.search;
    if (entries.types) filters.types = entries.types.split(',').map((type) => Number(type) as TransactionTypeValue);
    
    // Handle both 'accounts' and single 'account' parameter
    if (entries.accounts) filters.accounts = entries.accounts.split(',');
    if (entries.account && !entries.accounts) filters.accounts = [entries.account];
    
    // Handle both 'assets' and single 'asset' parameter
    if (entries.assets) filters.assets = entries.assets.split(',');
    if (entries.asset && !entries.assets) filters.assets = [entries.asset];
    
    // Standard flags handling
    if (entries.flags) filters.flags = entries.flags.split(',').map((flag) => flag as TransactionIssueType);
    
    if (entries.startDate || entries.endDate) {
        filters.dates = {
            start: entries.startDate,
            end: entries.endDate,
        };
    }

    return filters;
};

export const useTransactionState = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [transactionModalOpen, setTransactionModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
    const [isDetailedView, setIsDetailedView] = useState(false);
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<string>('date');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Initialize filters from URL
    const initialFilters = parseUrlToFilters(searchParams);
    const [filters, setFilters] = useState<TransactionFilters>(initialFilters);

    // Update URL when filters change
    const updateFilters = useCallback(
        (newFilters: TransactionFilters) => {
            const params: Record<string, string> = {};
            if (newFilters.search) params.search = newFilters.search;
            if (newFilters.types?.length) params.types = newFilters.types.join(',');
            if (newFilters.accounts?.length) params.accounts = newFilters.accounts.join(',');
            if (newFilters.assets?.length) params.assets = newFilters.assets.join(',');
            if (newFilters.flags?.length) params.flags = newFilters.flags.join(',');
            if (newFilters.dates?.start) params.startDate = newFilters.dates.start;
            if (newFilters.dates?.end) params.endDate = newFilters.dates.end;

            // Update the state first
            setFilters(newFilters);
            // Then update the URL
            setSearchParams(params, { replace: true });
        },
        [setSearchParams]
    );

    // Use a ref to track if the update is coming from URL or from user interaction
    const isUpdatingFromUrl = useRef(false);
    
    // Keep filters in sync with URL
    useEffect(() => {
        if (isUpdatingFromUrl.current) {
            isUpdatingFromUrl.current = false;
            return;
        }
        
        const urlFilters = parseUrlToFilters(searchParams);
        const urlFiltersStr = JSON.stringify(urlFilters);
        const filtersStr = JSON.stringify(filters);
        
        if (urlFiltersStr !== filtersStr) {
            isUpdatingFromUrl.current = true;
            setFilters(urlFilters);
        }
    }, [searchParams]);

    return {
        selectedIds,
        setSelectedIds,
        transactionModalOpen,
        setTransactionModalOpen,
        selectedTransaction,
        setSelectedTransaction,
        filters,
        setFilters: updateFilters,
        isDetailedView,
        setIsDetailedView,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
    };
};
